import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Alert } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

interface IdleInactivityModalProps {
  onLogout: () => void;
  idleTimeoutMinutes?: number;
  countdownSeconds?: number;
}

const IdleInactivityModal: React.FC<IdleInactivityModalProps> = ({
  onLogout,
  idleTimeoutMinutes = 30,
  countdownSeconds = 10,
}) => {
  const [isIdle, setIsIdle] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(countdownSeconds);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  // Reset activity timer
  const resetActivity = useCallback(() => {
    setLastActivity(Date.now());
    setIsIdle(false);
    setCountdown(countdownSeconds);
  }, [countdownSeconds]);

  // Handle user activity events
  const handleActivity = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  // Continue session (dismiss modal)
  const handleContinueSession = () => {
    resetActivity();
  };

  // Setup activity listeners
  useEffect(() => {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [handleActivity]);

  // Monitor idle time
  useEffect(() => {
    const checkIdleTime = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;

      if (timeSinceLastActivity >= idleTimeoutMs && !isIdle) {
        setIsIdle(true);
      }
    };

    const interval = setInterval(checkIdleTime, 1000);
    return () => clearInterval(interval);
  }, [lastActivity, idleTimeoutMinutes, isIdle]);

  // Countdown timer when modal is shown
  useEffect(() => {
    if (isIdle && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (isIdle && countdown === 0) {
      setIsIdle(false);
      onLogout();
    }
  }, [isIdle, countdown, onLogout]);

  return (
    <Modal show={isIdle} backdrop="static" keyboard={false} centered size="sm">
      <Modal.Header className="text-center border-0 pb-0">
        <Modal.Title className="w-100 text-center">
          <div className="text-warning mb-2">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="mx-auto"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
            </svg>
          </div>
          Session Timeout Warning
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="text-center">
        <Alert variant="warning" className="mb-3">
          You've been inactive for {idleTimeoutMinutes} minutes.
        </Alert>

        <div className="mb-3">
          <p className="mb-2">You will be automatically logged out in:</p>
          <div className="display-4 text-danger font-weight-bold">
            {countdown}
          </div>
          <small className="text-muted">seconds</small>
        </div>
      </Modal.Body>

      <Modal.Footer className="justify-content-center border-0">
        <Button
          variant="primary"
          onClick={handleContinueSession}
          className="me-2"
        >
          Continue Session
        </Button>
        <Button variant="outline-secondary" onClick={onLogout}>
          Logout Now
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IdleInactivityModal;
